/**
 * Kijiji regions we search for cleaners, and how to build a listing URL.
 *
 * Shared deliberately: the admin renders the picker from this list and the
 * scraper builds its requests from the same entries, so the two can never drift
 * into disagreeing about what "North Bay" means.
 *
 * The ids were read off live Kijiji, not guessed. Two things are worth knowing
 * before editing them:
 *
 *   - The `/b-<slug>/` part of the path is decorative. Kijiji resolves a page
 *     purely from the trailing `c<category>l<location>`, so a wrong slug does
 *     not 404, it silently serves a different category. That is exactly how the
 *     old placeholder path in this repo ended up pointing at "Business &
 *     Industrial Equipment" rather than cleaners.
 *   - Kijiji has no location ids for the small corridor towns. Burk's Falls,
 *     Sundridge, South River, Powassan and Callander all roll up into North Bay;
 *     Gravenhurst and Bracebridge into Muskoka. Searching the three regions
 *     below therefore covers all eleven service-area towns, and adding
 *     "Powassan" as its own entry is not possible rather than merely unhelpful.
 */

/** Kijiji's "Cleaners & Cleaning" category. */
export const CLEANING_CATEGORY = 160;

/**
 * Kijiji's "Apartments & Condos for Rent" category.
 *
 * Rentals are the property-management side: whoever is advertising a unit owns
 * or manages it, and the ones worth calling are the owners with a few doors who
 * are doing it themselves. The listing cannot say which is which, so these
 * arrive as leads to be qualified rather than as prospects already sorted.
 */
export const RENTALS_CATEGORY = 37;

export interface KijijiRegion {
  /** Stable id used in the API payload and the admin picker. */
  slug: string;
  label: string;
  /** Service-area towns this region covers, for the picker's help text. */
  towns: string[];
  /** Cleaner listings, page 1. */
  path: string;
  /** Rental listings, page 1 -- the property-management side. */
  rentalPath: string;
}

export const kijijiRegions: readonly KijijiRegion[] = [
  // Ordered nearest-first from Huntsville. Muskoka leads because it is home:
  // this region alone returns listings in Huntsville, Bracebridge and
  // Gravenhurst, which are the ones worth working first.
  {
    slug: "muskoka",
    label: "Muskoka",
    towns: ["Huntsville", "Bracebridge", "Gravenhurst"],
    path: `/b-cleaners-cleaning-service/muskoka/c${CLEANING_CATEGORY}l1700078`,
    rentalPath: `/b-apartments-condos/muskoka/c${RENTALS_CATEGORY}l1700078`,
  },
  {
    slug: "north-bay",
    label: "North Bay",
    towns: [
      "Burk's Falls",
      "Sundridge",
      "South River",
      "Powassan",
      "Callander",
      "North Bay",
    ],
    path: `/b-cleaners-cleaning-service/north-bay/c${CLEANING_CATEGORY}l1700243`,
    rentalPath: `/b-apartments-condos/north-bay/c${RENTALS_CATEGORY}l1700243`,
  },
  {
    slug: "barrie",
    label: "Barrie",
    towns: ["Barrie", "Orillia"],
    path: `/b-cleaners-cleaning-service/barrie/c${CLEANING_CATEGORY}l1700006`,
    rentalPath: `/b-apartments-condos/barrie/c${RENTALS_CATEGORY}l1700006`,
  },
] as const;

export const kijijiRegionSlugs = kijijiRegions.map((r) => r.slug);

export function regionBySlug(slug: string): KijijiRegion | undefined {
  return kijijiRegions.find((r) => r.slug === slug);
}

/**
 * Builds the URL for page `n` of a listing path.
 *
 * Kijiji puts the page in the path rather than the query string, immediately
 * before the `c<category>l<location>` segment. Page 1 is the bare path -- asking
 * for `/page-1/` redirects, and so does any page past the last one, which is
 * why the caller must decide how many pages exist from the payload's
 * `totalCount` rather than crawling until something fails.
 */
export function pagedPath(path: string, page: number): string {
  if (page <= 1) return path;
  const cut = path.lastIndexOf("/");
  return `${path.slice(0, cut)}/page-${page}${path.slice(cut)}`;
}
